import { Formik, Form } from 'formik';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { Segment, Header, Comment, Button } from 'semantic-ui-react';
import { useStore } from '../../../app/stores/store';
import { formatDistanceToNow } from 'date-fns';
import MyChatArea from '../../../app/common/form/MyChatArea';
import '../../../app/common/MediaQuery/responsive-view-page.css';
import { userLanguage } from '../../../app/common/userLanguageSchema/userLanguage';
interface Props {
  activityId: string;
  isOpen: any;
}

function useOnClickOutside(
  ref: any,
  handler: any,
  compareElement?: HTMLElement,
) {
  useEffect(
    () => {
      const listener = (event: any) => {
        // Do nothing if clicking ref's element or descendent elements
        if (
          !ref.current ||
          ref.current.contains(event.target) ||
          compareElement === document.activeElement
        ) {
          return;
        }
        handler(event);
      };
      document.addEventListener('click', listener);
      document.addEventListener('touchstart', listener);
      return () => {
        document.removeEventListener('click', listener);
        document.removeEventListener('touchstart', listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because the passed-in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    // eslint-disable-next-line
    [ref, handler],
  );
}

export default observer(function ActivityDetailedChat({
  activityId,
  isOpen,
}: Props) {
  const { commentStore, commonStore, userStore } = useStore();
  const chatRef = useRef(null);

  const closeChat = () => {
    const chatElement = document.getElementById(
      'chat-mobile',
    ) as HTMLDivElement;
    if (chatElement?.classList?.contains('show')) {
      isOpen.current = !isOpen.current;
      chatElement.classList.remove('show');
    }
  };

  useOnClickOutside(
    chatRef,
    () => closeChat(),
    document.getElementById('chat-mobile-btn') || undefined,
  );

  useEffect(() => {
    if (activityId) {
      commentStore.createHubConnection(activityId);
    }
    return () => {
      commentStore.clearComments();
    };
  }, [commentStore, activityId]);

  return (
    <div
      id="chat-mobile"
      ref={chatRef}
      className={
        commonStore.filterModeZero?.includes('AVLAVB11')
          ? 'activityDetailedChat component-isDisabled'
          : 'activityDetailedChat'
      }
    >
      <Segment
        textAlign="center"
        attached="top"
        inverted
        color="blue"
        style={{ border: 'none' }}
      >
        <Header
        //Chat about this event
        >
          {userStore.user?.customerName === 'RAI'
            ? 'Remarks'
            : (userLanguage[0].AssetsViewItemLevelViewPage.AVILVPS03 as any)?.[
                `${commonStore.language.key}`
              ]}
        </Header>
      </Segment>
      <Segment attached clearing>
        <Comment.Group className="commentScroll">
          {commentStore.comments.map((comment) => (
            <Comment style={{ minHeight: '38px' }} key={comment.id}>
              <Comment.Avatar
                src={comment.image || '/assets/user.png'}
                onError={(img: any) => (img.target.src = '/assets/user.png')}
              />
              <Comment.Content style={{ display: 'flex' }}>
                <Comment.Author>{comment.displayName}</Comment.Author>
                <Comment.Metadata>
                  <div
                  //ago
                  >
                    {formatDistanceToNow(comment.createdAt)}{' '}
                    {
                      (
                        userLanguage[0].AssetsViewItemLevelViewPageChat
                          .AVILVPCL01 as any
                      )?.[`${commonStore.language.key}`]
                    }
                  </div>
                </Comment.Metadata>
              </Comment.Content>
              <Comment.Text style={{ marginLeft: '50px' }}>
                {comment.body}
              </Comment.Text>
            </Comment>
          ))}
        </Comment.Group>
        <Formik
          onSubmit={(values, { resetForm }) => {
            commentStore.addComment(values).then(() => resetForm());
          }}
          initialValues={{ itemLevelId: '', body: '' }}
        >
          {({ isSubmitting }) => (
            <Form className="ui form">
              <MyChatArea
                placeholder={
                  (
                    userLanguage[0].AssetsViewItemLevelViewPageChat
                      .AVILVPCTA01 as any
                  )?.[`${commonStore.language.key}`]
                } //"Add Comment"
                name="body"
                rows={8}
              />
              <Button
                loading={isSubmitting}
                floated="right"
                content={
                  (
                    userLanguage[0].AssetsViewItemLevelViewPageChat
                      .AVILVPCB01 as any
                  )?.[`${commonStore.language.key}`]
                } //"Send"
                labelPosition="left"
                icon="send"
                primary
                type="submit"
              />
            </Form>
          )}
        </Formik>
      </Segment>
    </div>
  );
});
